import React, { useEffect } from 'react';
import { View, StyleSheet, ViewStyle, DimensionValue } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  interpolate,
} from 'react-native-reanimated';

interface SkeletonProps {
  width?: DimensionValue;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
}

export function Skeleton({ width = '100%', height = 20, borderRadius = 4, style }: SkeletonProps) {
  const shimmer = useSharedValue(0);

  useEffect(() => {
    shimmer.value = withRepeat(
      withTiming(1, { duration: 1200 }),
      -1,
      false
    );
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      shimmer.value,
      [0, 0.5, 1],
      [0.3, 0.5, 0.3]
    );

    return {
      opacity,
    };
  });

  return (
    <Animated.View
      style={[
        styles.skeleton,
        {
          width,
          height,
          borderRadius,
        },
        animatedStyle,
        style,
      ]}
    />
  );
}

export function SkeletonCircle({ size = 40, style }: { size?: number; style?: ViewStyle }) {
  return (
    <Skeleton
      width={size}
      height={size}
      borderRadius={size / 2}
      {...(style && { style })}
    />
  );
}

/**
 * SkeletonPost — white card (`#FFFFFF`) rendered on the beige community feed
 * (`#F9F8F3`). Matches the real `postCard` style in `app/community.tsx`.
 * Surface: white (#FFFFFF).
 */
export function SkeletonPost() {
  return (
    <View style={styles.postSkeleton}>
      <View style={styles.avatarColumn}>
        <SkeletonCircle size={32} />
      </View>
      <View style={styles.contentColumn}>
        <View style={styles.header}>
          <Skeleton width={100} height={14} />
          <Skeleton width={40} height={12} />
        </View>
        <Skeleton width="100%" height={16} style={{ marginTop: 8 }} />
        <Skeleton width="90%" height={16} style={{ marginTop: 4 }} />
        <Skeleton width="70%" height={16} style={{ marginTop: 4 }} />
        <View style={styles.engagement}>
          <Skeleton width={60} height={16} />
          <Skeleton width={60} height={16} />
        </View>
      </View>
    </View>
  );
}

/**
 * SkeletonComment — white card (`#FFFFFF`) intended for the comments view
 * on a community post. Surface matches the white background of post detail
 * screens. Surface: white (#FFFFFF).
 */
export function SkeletonComment() {
  return (
    <View style={styles.commentSkeleton}>
      <View style={styles.avatarColumn}>
        <SkeletonCircle size={32} />
      </View>
      <View style={styles.contentColumn}>
        <Skeleton width={80} height={14} />
        <Skeleton width="100%" height={14} style={{ marginTop: 8 }} />
        <Skeleton width="60%" height={14} style={{ marginTop: 4 }} />
      </View>
    </View>
  );
}

export function SkeletonMessage({ isUser = false }: { isUser?: boolean }) {
  return (
    <View style={[styles.messageSkeleton, isUser && styles.messageSkeletonUser]}>
      {!isUser && <SkeletonCircle size={32} style={{ marginRight: 12 }} />}
      <View style={styles.messageContent}>
        <Skeleton width="80%" height={16} />
        <Skeleton width="60%" height={16} style={{ marginTop: 4 }} />
      </View>
    </View>
  );
}

/**
 * SkeletonMeetingCard — beige card (`#E7E6E0`) rendered on the meetings
 * screen (`app/encontros-online.tsx`), which has a beige container
 * (`#F9F8F3`). Uses `#D0CFC9` shimmer bars consistent with the beige
 * design-system surface. Surface: beige (#E7E6E0).
 */
export function SkeletonMeetingCard() {
  return (
    <View style={styles.meetingCardSkeleton}>
      <Skeleton width={60} height={60} borderRadius={8} style={{ backgroundColor: '#D0CFC9' }} />
      <View style={styles.meetingInfo}>
        <Skeleton width="70%" height={18} style={{ backgroundColor: '#D0CFC9' }} />
        <Skeleton width="100%" height={14} style={{ marginTop: 8, backgroundColor: '#D0CFC9' }} />
        <Skeleton width="50%" height={14} style={{ marginTop: 4, backgroundColor: '#D0CFC9' }} />
      </View>
    </View>
  );
}

/**
 * SkeletonNotification — white card (`#FFFFFF`) rendered on the notifications
 * screen (`app/notifications.tsx`), which has a white container. Matches
 * the `notification` row background. Surface: white (#FFFFFF).
 */
export function SkeletonNotification() {
  return (
    <View style={styles.notificationSkeleton}>
      <View style={styles.notificationIconContainer}>
        <SkeletonCircle size={48} />
      </View>
      <View style={styles.notificationContent}>
        <Skeleton width="60%" height={16} />
        <Skeleton width="100%" height={14} style={{ marginTop: 8 }} />
        <Skeleton width="80%" height={14} style={{ marginTop: 4 }} />
        <Skeleton width="30%" height={12} style={{ marginTop: 8 }} />
      </View>
    </View>
  );
}

/**
 * SkeletonMissoes — placeholder for the Recovery Journey "Missões" tab in
 * `app/diary.tsx`. Uses the same simple beige-square pattern as the trail,
 * notes, clock, and meetings pages: plain `#E7E6E0` rectangles, no shimmer,
 * no inner white cards. Mirrors the rendered surface's silhouette: greeting
 * row + stage chip → goal card → 7-day Encontros strip → today header →
 * today's mission list.
 */
export function SkeletonMissoes() {
  return (
    <View style={styles.missoesSkeleton}>
      {/* Greeting row — left title block + right stage chip */}
      <View style={styles.missoesGreetingRow}>
        <View style={styles.missoesGreetingTitle} />
        <View style={styles.missoesGreetingChip} />
      </View>

      {/* Goal card silhouette — single beige rectangle */}
      <View style={styles.missoesGoalCard} />

      {/* Encontros 7-day strip — 7 round day pills */}
      <View style={styles.missoesWeekStrip}>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <View key={i} style={styles.missoesDayPillWrapper}>
            <View style={styles.missoesDayLabel} />
            <View style={styles.missoesDayPill} />
          </View>
        ))}
      </View>

      {/* Today header — small avatar slot + date label */}
      <View style={styles.missoesTodayHeader}>
        <View style={styles.missoesTodayAvatar} />
        <View style={styles.missoesTodayDate} />
      </View>

      {/* Today missions — section heading + 5 stacked mission rows */}
      <View style={styles.missoesTodayBlock}>
        <View style={styles.missoesSectionHeading} />
        {[0, 1, 2, 3, 4].map((i) => (
          <View key={i} style={styles.missoesMissionRow}>
            <View style={styles.missoesMissionCheck} />
            <View style={styles.missoesMissionLabel} />
          </View>
        ))}
      </View>
    </View>
  );
}

/**
 * SkeletonSponsorCard — beige card (`#F9F8F3`) rendered on the sponsors list
 * screen (`app/sponsors/index.tsx`). Matches the real `sponsorCard` style
 * which uses `backgroundColor: '#F9F8F3'`. Surface: beige (#F9F8F3).
 *
 * NOTE: was previously `#FFFFFF` (white) — corrected to match the real card
 * background and the beige design-system surface.
 */
export function SkeletonSponsorCard() {
  return (
    <View style={styles.sponsorCardSkeleton}>
      {/* Header with Avatar and Info */}
      <View style={styles.sponsorHeader}>
        <SkeletonCircle size={60} />
        <View style={styles.sponsorInfo}>
          <Skeleton width={120} height={18} />
          <Skeleton width={80} height={14} style={{ marginTop: 4 }} />
        </View>
      </View>
      
      {/* Bio */}
      <Skeleton width="100%" height={14} style={{ marginTop: 16 }} />
      <Skeleton width="90%" height={14} style={{ marginTop: 4 }} />
      <Skeleton width="70%" height={14} style={{ marginTop: 4 }} />
      
      {/* Interest Pills */}
      <View style={styles.sponsorPills}>
        <Skeleton width={60} height={24} borderRadius={12} />
        <Skeleton width={70} height={24} borderRadius={12} />
        <Skeleton width={55} height={24} borderRadius={12} />
        <Skeleton width={65} height={24} borderRadius={12} />
      </View>
      
      {/* Schedule Button */}
      <Skeleton width="100%" height={48} borderRadius={24} style={{ marginTop: 16 }} />
    </View>
  );
}

const styles = StyleSheet.create({
  skeleton: {
    backgroundColor: '#F0EEE4',
  },
  postSkeleton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E7EAED',
  },
  avatarColumn: {
    alignItems: 'center',
  },
  contentColumn: {
    flex: 1,
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  engagement: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  commentSkeleton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 12,
    gap: 12,
    backgroundColor: '#FFFFFF',
  },
  messageSkeleton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  messageSkeletonUser: {
    justifyContent: 'flex-end',
  },
  messageContent: {
    flex: 1,
    maxWidth: '80%',
  },
  meetingCardSkeleton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 16,
    backgroundColor: '#E7E6E0',
    borderRadius: 12,
  },
  meetingInfo: {
    flex: 1,
  },
  notificationSkeleton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F5F5F5',
  },
  notificationIconContainer: {
    alignItems: 'center',
  },
  notificationContent: {
    flex: 1,
  },
  sponsorCardSkeleton: {
    backgroundColor: '#F9F8F3',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 24,
    marginBottom: 16,
  },
  sponsorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sponsorInfo: {
    flex: 1,
  },
  sponsorPills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  missoesSkeleton: {
    paddingTop: 8,
  },
  missoesGreetingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 4,
  },
  missoesGreetingTitle: {
    width: 180,
    height: 24,
    backgroundColor: '#E7E6E0',
    borderRadius: 8,
  },
  missoesGreetingChip: {
    width: 90,
    height: 33,
    backgroundColor: '#E7E6E0',
    borderRadius: 17,
  },
  missoesGoalCard: {
    marginHorizontal: 24,
    marginTop: 12,
    height: 168,
    backgroundColor: '#E7E6E0',
    borderRadius: 12,
  },
  missoesWeekStrip: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    marginTop: 24,
  },
  missoesDayPillWrapper: {
    alignItems: 'center',
  },
  missoesDayLabel: {
    width: 20,
    height: 10,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  missoesDayPill: {
    width: 32,
    height: 32,
    backgroundColor: '#E7E6E0',
    borderRadius: 16,
    marginTop: 6,
  },
  missoesTodayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    marginTop: 24,
  },
  missoesTodayAvatar: {
    width: 32,
    height: 32,
    backgroundColor: '#E7E6E0',
    borderRadius: 16,
  },
  missoesTodayDate: {
    width: 140,
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
    marginLeft: 16,
  },
  missoesTodayBlock: {
    paddingLeft: 56,
    paddingRight: 24,
    marginTop: 16,
  },
  missoesSectionHeading: {
    width: 160,
    height: 16,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  missoesMissionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    height: 24,
  },
  missoesMissionCheck: {
    width: 16,
    height: 16,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  missoesMissionLabel: {
    flex: 1,
    maxWidth: '60%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
    marginLeft: 8,
  },
});
